import { useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useFetchPutV2 } from "hooks/fetch/useFetchPutV2";
import makeStyles from "@mui/styles/makeStyles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ProgressIndicatorOverlay from "components/ProgressIndicatorOverlay";
import ReportForm from "components/CaseManagement/CaseForms/ReportForm";
import EventModelState from "components/CaseManagement/State/EventModelState";
import { isNil } from "lodash";
import { uploadFile } from "core/blob_operations";
import { CognitoAuthContext } from "components/Authentication/CognitoAuthContext";
import styles from "assets/jss/default/components/newCaseWithDiagnosticDialogStyle.js";
import { CaseContext } from "contexts/CaseContext";
import omit from "lodash/omit";
import { siteSettings } from "core/siteSettings";
import { AxiosContext } from "contexts/AxiosContext";
import { useQueryClient } from "@tanstack/react-query";

const useStyles = makeStyles(styles);

export default function ReportUpdateDialog(props) {
  const {
    open,
    setOpen,
    reportName,
    reportType,
    report,
    refetchEvent,
    refetchStream
  } = props;
  const { axios } = useContext(AxiosContext);
  const queryClient = useQueryClient();

  const classes = useStyles();

  const dialogTitle = `Edit ${reportName}`;

  const [eventModel, setEventModel] = useState(
    EventModelState.createWithContent(report)
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState();
  const [updatingAttachments, setUpdatingAttachments] = useState(false);
  const [attachmentIdsToDelete, setAttachmentIdsToDelete] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [filesDropping, setFilesDropping] = useState(false);

  const { refetchCase } = useContext(CaseContext);

  const { getToken } = useContext(CognitoAuthContext);

  const eventId = eventModel.toJS().id;
  const {
    loading: updateEventInProgress,
    putCallback: updateEvent
  } = useFetchPutV2(`events/${eventId}`, isNil(eventId));

  const onEventUpdated = async updateEvents => {
    setUpdatingAttachments(true);

    try {
      const baseApiUrl = siteSettings.siteApiURL;

      const operations = [];

      if (filesToUpload && filesToUpload.length > 0) {
        filesToUpload.forEach(file => {
          if (!file.uploaded) {
            operations.push(
              uploadFile(
                `${baseApiUrl}/events/${updateEvents.id}/attachments`,
                file.contents,
                getToken
              )
            );
          }
        });
      }

      if (attachmentIdsToDelete && attachmentIdsToDelete.length > 0) {
        attachmentIdsToDelete.forEach(attachmentId => {
          operations.push(
            axios.post(
              `events/${updateEvents.id}/attachments/detach/${attachmentId}`
            )
          );
        });
      }

      if (operations.length > 0) {
        await Promise.all(operations);
      }
      await queryClient.invalidateQueries("attachments");
      await Promise.all([refetchEvent(), refetchStream(), refetchCase()]);
    } catch (error) {
      console.error(error);
    } finally {
      resetData();
      setUpdatingAttachments(false);
      setOpen(false);
    }
  };

  const resetData = async () => {
    setFilesToUpload([]);
    setAttachmentIdsToDelete([]);
    setEventModel(EventModelState.createWithContent(report));
    setIsFormValid(false);
  };

  const cancel = () => {
    resetData();
    setOpen(false);
  };

  const onReportFormChange = useCallback(
    ({ eventModel: updatedEventModel, severityStatus, isValid }) => {
      setSelectedStatusId(severityStatus);
      setEventModel(EventModelState.set(eventModel, updatedEventModel));
      setIsFormValid(isValid);
    },
    [eventModel]
  );

  const updateReport = async () => {
    let event = eventModel.toJS();

    event.case_severity_type = selectedStatusId;
    event = omit(event, "__rawAttachments");

    try {
      const updatedEvent = await updateEvent(event);
      await onEventUpdated(updatedEvent);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="sm"
      onClose={() => cancel()}
      aria-labelledby="report-update-dialog"
      data-qa="CP-Dialog-Update-Report"
    >
      <ProgressIndicatorOverlay
        loading={updateEventInProgress || updatingAttachments}
      />
      <DialogTitle>
        <Grid container direction="row" alignItems="center">
          <Grid item xs={12} md={12}>
            {dialogTitle}
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <PerfectScrollbar>
          <ReportForm
            key={dialogTitle}
            onFormChange={onReportFormChange}
            variant={reportType}
            eventModel={eventModel.toJS()}
            severityStatus={report.case_severity_type}
            update={true}
            attachmentsToUpload={filesToUpload}
            setAttachmentsToUpload={setFilesToUpload}
            attachmentIdsToDelete={attachmentIdsToDelete}
            setAttachmentIdsToDelete={setAttachmentIdsToDelete}
            filesDropping={filesDropping}
            setFilesDropping={setFilesDropping}
          />
        </PerfectScrollbar>
      </DialogContent>
      <DialogActions className={classes.buttonArea}>
        <Button
          className={classes.cancelButton}
          variant="outlined"
          color="primary"
          onClick={() => cancel()}
          data-qa="CP-Button-Cancel-Update-Report"
        >
          Cancel
        </Button>
        <Button
          className={classes.completeButton}
          disabled={
            updateEventInProgress ||
            updatingAttachments ||
            !isFormValid ||
            filesDropping
          }
          variant="contained"
          color="primary"
          onClick={() => updateReport()}
          data-qa="CP-Button-Complete-Update-Report"
        >
          Complete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ReportUpdateDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  reportName: PropTypes.string.isRequired,
  reportType: PropTypes.oneOf(["inspection", "diagnosis"]).isRequired,
  report: PropTypes.object,
  refetchEvent: PropTypes.func,
  refetchStream: PropTypes.func,
  caseId: PropTypes.number
};
